
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'framer-motion';
import CountdownUnit from './CountdownUnit';
import styles from './MobileScarcityBanner.module.css';
import Image from 'next/image';
import { useLayout } from '@/contexts/layout';
import { getStrapiURL } from '@/api/api';

const RESERVATION_FLOW_PAGES = [
  '/veiculos',
  '/extras',
  '/condutor',
  '/pagamento',
  '/login'
];

const calculateTimeLeft = (expirationTime: number) => {
  const difference = expirationTime - new Date().getTime();
  let timeLeft = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
};

export default function MobileScarcityBanner() {
  const router = useRouter();
  const { rate_exp } = router.query;
  const expirationTime = Number(rate_exp);

  const { setIsScarcityBannerVisible, scarcityBannerSettings } = useLayout();
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expirationTime));
  const [showYellowBanner, setShowYellowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [userHasClosed, setUserHasClosed] = useState(false);

  useEffect(() => {
    setUserHasClosed(false);
  }, [rate_exp]);

  const handleClose = () => {
    setUserHasClosed(true);
    setIsVisible(false);
    setIsScarcityBannerVisible(false);
  };

  useEffect(() => {
    if (!scarcityBannerSettings || !rate_exp || isNaN(expirationTime)) {
      setIsVisible(false);
      setIsScarcityBannerVisible(false);
      return;
    }

    const checkVisibility = () => {
      const isOnReservationFlow = RESERVATION_FLOW_PAGES.some(path => router.pathname.startsWith(path));
      const timeRemaining = expirationTime - Date.now();
      const activationTimeMs = scarcityBannerSettings.activation_time_minutes * 60 * 1000;

      const shouldBeVisible =
        isOnReservationFlow &&
        !userHasClosed &&
        timeRemaining > 0 &&
        timeRemaining <= activationTimeMs;

      setIsVisible(shouldBeVisible);
      setIsScarcityBannerVisible(shouldBeVisible);
      setTimeLeft(calculateTimeLeft(expirationTime));

      return timeRemaining > 0;
    };

    if (!checkVisibility()) {
      return;
    }

    const timer = setInterval(() => {
      if (!checkVisibility()) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [router.pathname, rate_exp, expirationTime, scarcityBannerSettings, setIsScarcityBannerVisible, userHasClosed]);


  useEffect(() => {
    if (!isVisible) {
      setShowYellowBanner(false);
      return;
    }
    const yellowBannerTimer = setTimeout(() => {
      setShowYellowBanner(true);
    }, 5000);
    return () => clearTimeout(yellowBannerTimer);
  }, [isVisible]);

  const formatDigit = (num: number) => num.toString().padStart(2, '0');

  if (!isVisible || !scarcityBannerSettings) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className={styles.bannerContainer}
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
      >
        <div
          className={styles.topSection}
          style={{
            background: scarcityBannerSettings.main_banner_background_color,
            color: scarcityBannerSettings.main_banner_text_color
          }}
        >
          <button onClick={handleClose} className={styles.closeButton} aria-label="Fechar banner">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M7.15385 0.923105L1.00002 7.07693M1 0.92308L7.15385 7.07693" stroke="#939598" strokeWidth="0.984615" strokeLinecap="round" />
            </svg>
          </button>
          <div
            className={`${styles.backgroundCircle} ${styles.circle1}`}
            style={{ backgroundColor: scarcityBannerSettings.decorative_circles_color + 'A6' }}
          />
          <div
            className={`${styles.backgroundCircle} ${styles.circle2}`}
            style={{ borderColor: scarcityBannerSettings.decorative_circles_color }}
          />
          <div
            className={`${styles.backgroundCircle} ${styles.circle3}`}
            style={{ backgroundColor: scarcityBannerSettings.decorative_circles_color + 'A6' }}
          />
          <div className={styles.countdownSection}>
            <CountdownUnit
              digit={formatDigit(timeLeft.days)}
              label="dias"
              backgroundColor={scarcityBannerSettings.countdown_background_color}
              borderColor={scarcityBannerSettings.countdown_border_color}
              digitColor={scarcityBannerSettings.countdown_digit_color}
              labelColor={scarcityBannerSettings.countdown_label_color}
            />
            <span className={styles.separator} style={{ color: scarcityBannerSettings.countdown_separator_color }}>:</span>
            <CountdownUnit
              digit={formatDigit(timeLeft.hours)}
              label="horas"
              backgroundColor={scarcityBannerSettings.countdown_background_color}
              borderColor={scarcityBannerSettings.countdown_border_color}
              digitColor={scarcityBannerSettings.countdown_digit_color}
              labelColor={scarcityBannerSettings.countdown_label_color}
            />
            <span className={styles.separator} style={{ color: scarcityBannerSettings.countdown_separator_color }}>:</span>
            <CountdownUnit
              digit={formatDigit(timeLeft.minutes)}
              label="minutos"
              backgroundColor={scarcityBannerSettings.countdown_background_color}
              borderColor={scarcityBannerSettings.countdown_border_color}
              digitColor={scarcityBannerSettings.countdown_digit_color}
              labelColor={scarcityBannerSettings.countdown_label_color}
            />
            <span className={styles.separator} style={{ color: scarcityBannerSettings.countdown_separator_color }}>:</span>
            <CountdownUnit
              digit={formatDigit(timeLeft.seconds)}
              label="segundos"
              backgroundColor={scarcityBannerSettings.countdown_background_color}
              borderColor={scarcityBannerSettings.countdown_border_color}
              digitColor={scarcityBannerSettings.countdown_digit_color}
              labelColor={scarcityBannerSettings.countdown_label_color}
            />
          </div>
          <AnimatePresence>
            {!showYellowBanner && (
              <motion.div
                key="scarcity-text-mobile"
                className={styles.textSection}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <p className={styles.mainText}>{scarcityBannerSettings.title}</p>
                <p className={styles.subText}>{scarcityBannerSettings.description}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {showYellowBanner && (
            <motion.div
              className={styles.bottomSection}
              style={{ backgroundColor: scarcityBannerSettings.secondary_banner_background_color }}
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: "0%", opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1], delay: 0.3 }}
            >
              <div className={styles.carImageWrapper}>
                {scarcityBannerSettings.vehicle_image &&
                  <Image src={getStrapiURL(scarcityBannerSettings.vehicle_image.url)} alt="Carro" layout="fill" objectFit="contain" />
                }
              </div>
              <span className={styles.yellowBannerText} style={{ color: scarcityBannerSettings.secondary_banner_text_color }}>
                {scarcityBannerSettings.secondary_banner_text}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
